import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useApp } from '../context';
import {
  ArrowLeft,
  Bell,
  Cake,
  CalendarCheck,
  UserCheck,
  MessageSquare,
  AlertTriangle,
} from 'lucide-react';
import { cn } from '../lib/utils';

interface ToggleProps {
  enabled: boolean;
  onChange: (val: boolean) => void;
  disabled?: boolean;
}

function Toggle({ enabled, onChange, disabled }: ToggleProps) {
  return (
    <button
      onClick={() => !disabled && onChange(!enabled)}
      className={cn(
        'w-12 h-7 rounded-full transition-colors relative flex-shrink-0',
        disabled ? 'opacity-40 cursor-not-allowed' : 'cursor-pointer',
        enabled ? 'bg-[#fa8585]' : 'bg-slate-300',
      )}
    >
      <div
        className={cn(
          'absolute top-0.5 w-6 h-6 bg-white rounded-full shadow transition-transform',
          enabled ? 'translate-x-5' : 'translate-x-0.5',
        )}
      />
    </button>
  );
}

export default function NotificationSettings() {
  const navigate = useNavigate();
  const { state, updateNotificationSettings } = useApp();
  const ns = state.notificationSettings;

  // Master toggle is local — it disables all categories visually but doesn't persist individually
  const [notificationsEnabled, setNotificationsEnabled] = useState(
    ns.dailyReminders || ns.birthdayReminders || ns.weeklySummary || ns.overdueAlerts,
  );

  // Track browser permission state to show blocked banner
  const [permissionState, setPermissionState] = useState<NotificationPermission | 'unsupported'>(
    'Notification' in window ? Notification.permission : 'unsupported',
  );

  // Re-check permission when the page becomes visible (user may have changed it in settings)
  useEffect(() => {
    const handleVisibility = () => {
      if (!document.hidden && 'Notification' in window) {
        setPermissionState(Notification.permission);
      }
    };
    document.addEventListener('visibilitychange', handleVisibility);
    return () => document.removeEventListener('visibilitychange', handleVisibility);
  }, []);

  const isBlocked = permissionState === 'denied';
  const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent);

  const notifCategories = [
    {
      icon: Bell,
      label: 'Daily Reminders',
      desc: 'Morning alerts when contacts need your attention',
      enabled: ns.dailyReminders,
      onChange: (val: boolean) => updateNotificationSettings({ dailyReminders: val }),
      iconBg: 'bg-red-50',
      iconColor: 'text-[#fa8585]',
    },
    {
      icon: Cake,
      label: 'Birthday Reminders',
      desc: "Get notified before a contact's birthday",
      enabled: ns.birthdayReminders,
      onChange: (val: boolean) => updateNotificationSettings({ birthdayReminders: val }),
      iconBg: 'bg-pink-50',
      iconColor: 'text-pink-500',
    },
    {
      icon: CalendarCheck,
      label: 'Weekly Summary',
      desc: 'Sunday summary of your relationship health',
      enabled: ns.weeklySummary,
      onChange: (val: boolean) => updateNotificationSettings({ weeklySummary: val }),
      iconBg: 'bg-blue-50',
      iconColor: 'text-blue-500',
    },
    {
      icon: UserCheck,
      label: 'Overdue Contact Alerts',
      desc: "Evening nudges when you haven't reached someone",
      enabled: ns.overdueAlerts,
      onChange: (val: boolean) => updateNotificationSettings({ overdueAlerts: val }),
      iconBg: 'bg-red-50',
      iconColor: 'text-[#fa8585]',
    },
  ];

  const handleMasterToggle = (enabled: boolean) => {
    setNotificationsEnabled(enabled);
    if (!enabled) {
      updateNotificationSettings({
        dailyReminders: false,
        birthdayReminders: false,
        weeklySummary: false,
        overdueAlerts: false,
      });
    } else {
      updateNotificationSettings({
        dailyReminders: true,
        birthdayReminders: true,
        weeklySummary: true,
        overdueAlerts: true,
      });
      // Request push permission if not yet granted
      if ('Notification' in window) {
        if (Notification.permission === 'default') {
          import('../lib/firebase').then(({ requestNotificationPermission }) => {
            requestNotificationPermission()
              .then(() => setPermissionState(Notification.permission))
              .catch(() => {});
          });
        } else if (Notification.permission === 'denied') {
          // Permission was previously denied — update state so banner shows
          setPermissionState('denied');
        }
      }
    }
  };

  return (
    <div className="relative flex min-h-screen w-full flex-col bg-[#f8f5f5] overflow-x-hidden">
      {/* Header */}
      <div className="flex items-center p-4 pb-2 justify-between sticky top-0 bg-[#f8f5f5]/80 backdrop-blur-md z-10">
        <button
          onClick={() => navigate(-1)}
          className="size-10 rounded-full hover:bg-slate-200/50 flex items-center justify-center transition-colors"
        >
          <ArrowLeft size={24} className="text-slate-600" />
        </button>
        <h1 className="text-lg font-bold text-slate-900">Notifications</h1>
        <div className="size-10" />
      </div>

      <div className="px-5 pb-8 space-y-5 mt-2">
        {/* Master Notifications Toggle */}
        <div className="bg-white rounded-2xl p-5 shadow-sm">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <div className="size-11 rounded-xl bg-[#fa8585]/10 flex items-center justify-center">
                <Bell size={22} className="text-[#fa8585]" />
              </div>
              <div>
                <p className="font-bold text-slate-900">Notifications</p>
                <p className="text-xs text-slate-400 mt-0.5">
                  {notificationsEnabled ? 'All notifications active' : 'All notifications paused'}
                </p>
              </div>
            </div>
            <Toggle enabled={notificationsEnabled} onChange={handleMasterToggle} />
          </div>
        </div>

        {/* Blocked Permission Banner */}
        {isBlocked && (
          <div className="bg-amber-50 border border-amber-200 rounded-2xl p-4 flex gap-3">
            <AlertTriangle size={20} className="text-amber-500 shrink-0 mt-0.5" />
            <div>
              <p className="text-sm font-bold text-amber-800">Notifications are blocked</p>
              <p className="text-xs text-amber-700 mt-1 leading-relaxed">
                {isIOS
                  ? 'To re-enable, go to Settings > Apps > Nudge > Notifications and turn them on.'
                  : 'To re-enable, tap the lock icon in your browser\'s address bar, find "Notifications", and change it to "Allow".'}
              </p>
            </div>
          </div>
        )}

        {/* Notification Categories */}
        <div>
          <h2 className="text-xs font-bold text-slate-400 uppercase tracking-widest px-2 mb-3">
            Notification Categories
          </h2>
          <div className="bg-white rounded-2xl shadow-sm divide-y divide-slate-100">
            {notifCategories.map((cat) => (
              <div key={cat.label} className="flex items-center gap-4 p-4">
                <div
                  className={cn(
                    'size-10 rounded-xl flex items-center justify-center flex-shrink-0',
                    cat.iconBg,
                  )}
                >
                  <cat.icon size={20} className={cat.iconColor} />
                </div>
                <div className="flex-1 min-w-0">
                  <p
                    className={cn(
                      'text-sm font-bold',
                      notificationsEnabled ? 'text-slate-800' : 'text-slate-400',
                    )}
                  >
                    {cat.label}
                  </p>
                  <p className="text-[11px] text-slate-400 mt-0.5 leading-relaxed">{cat.desc}</p>
                </div>
                <Toggle
                  enabled={cat.enabled}
                  onChange={cat.onChange}
                  disabled={!notificationsEnabled}
                />
              </div>
            ))}
          </div>
        </div>

        {/* Quiet Hours */}
        <div>
          <h2 className="text-xs font-bold text-slate-400 uppercase tracking-widest px-2 mb-3">
            Schedule
          </h2>
          <div className="bg-white rounded-2xl p-4 shadow-sm">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-4">
                <div className="size-10 rounded-xl bg-indigo-50 flex items-center justify-center">
                  <MessageSquare size={20} className="text-indigo-500" />
                </div>
                <div>
                  <p
                    className={cn(
                      'text-sm font-bold',
                      notificationsEnabled ? 'text-slate-800' : 'text-slate-400',
                    )}
                  >
                    Quiet Hours
                  </p>
                  <p className="text-[11px] text-slate-400 mt-0.5">No notifications 10 PM – 8 AM</p>
                </div>
              </div>
              <Toggle
                enabled={ns.quietHours.enabled}
                onChange={(val) =>
                  updateNotificationSettings({ quietHours: { ...ns.quietHours, enabled: val } })
                }
                disabled={!notificationsEnabled}
              />
            </div>
          </div>
        </div>

        {/* Info Note */}
        <p className="text-center text-xs text-slate-400 pt-2 leading-relaxed">
          We'll never spam you. Nudge only sends helpful reminders to strengthen your relationships.
        </p>
      </div>
    </div>
  );
}
